// CONTABILIDAD DE LA TIENDA DE PERSONALIZABLES
//
// Vive entera en Firestore, sin hojas externas:
//   · `packs_ledger`   — ingresos. Una fila por cobro confirmado.
//   · `packs_expenses` — gastos. Concepto e importe.
//
// El apunte de ingreso lo crea la Cloud Function `packsShop_adminSetStatus`
// cuando el admin confirma que ha recibido el pago —nunca antes—, usando el id
// del pedido como id del apunte para que reconfirmar no pueda duplicar la fila.
// A partir de ahí, cada cambio de estado del pedido le sincroniza el `estado`.
//
// Las dos colecciones son solo-admin, así que las filas manuales y los gastos
// se escriben directo desde el cliente con la regla `isAdmin()`.

import { useMemo, useState } from 'react';
import { collection, query, orderBy } from 'firebase/firestore';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend, Line, ComposedChart,
} from 'recharts';
import { db } from '@/firebase/config';
import { useCollection } from '@/firebase/firestore/use-collection';
import { sanitizeFirestoreData } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import type { PackLedgerEntry, PackExpense, PackOrder } from '@/lib/types';
import {
  createLedgerEntry, updateLedgerEntry, deleteLedgerEntry,
  createExpense, updateExpense, deleteExpense,
} from '@/lib/actions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { ESTADOS, hexToRgba, toMs } from '@/components/tienda/ui';
import {
  Loader2, Plus, Pencil, Trash2, TrendingUp, TrendingDown, Search,
  ArrowUpDown, Receipt, BarChart3, PieChart as PieIcon, Download, Coins,
  Package, Building2,
} from 'lucide-react';
import { motion } from 'framer-motion';

const PALETA = ['#34d399', '#38bdf8', '#a78bfa', '#fbbf24', '#f472b6', '#22d3ee', '#fb7185', '#4ade80'];

const eur = (n: number) =>
  (Number(n) || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });

const fechaCorta = (v: any) => {
  const ms = toMs(v);
  return ms ? new Date(ms).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
};

/** `yyyy-mm-dd` para los `<input type="date">`. */
const aInputDate = (v: any) => {
  const ms = toMs(v);
  const d = ms ? new Date(ms) : new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const METODOS = [
  { key: 'bizum', label: 'Bizum', color: '#38bdf8' },
  { key: 'transferencia', label: 'Transferencia', color: '#fbbf24' },
  { key: 'otro', label: 'Otro', color: '#a78bfa' },
] as const;

const metodoLabel = (m: string) => METODOS.find((x) => x.key === m)?.label || 'Otro';
const metodoColor = (m: string) => METODOS.find((x) => x.key === m)?.color || '#a78bfa';

export function Contabilidad({ orders }: { orders: PackOrder[] }) {
  const [vista, setVista] = useState<'ingresos' | 'gastos'>('ingresos');

  const ledgerQuery = useMemo(() => query(collection(db, 'packs_ledger'), orderBy('fechaPago', 'desc')), []);
  const { data: rawLedger, isLoading: cargandoL } = useCollection<PackLedgerEntry>(ledgerQuery);
  const ledger = useMemo(
    () => (rawLedger || []).map((e) => sanitizeFirestoreData(e) as PackLedgerEntry),
    [rawLedger],
  );

  const gastosQuery = useMemo(() => query(collection(db, 'packs_expenses'), orderBy('createdAt', 'desc')), []);
  const { data: rawGastos, isLoading: cargandoG } = useCollection<PackExpense>(gastosQuery);
  const gastos = useMemo(
    () => (rawGastos || []).map((e) => sanitizeFirestoreData(e) as PackExpense),
    [rawGastos],
  );

  // ── Números ──
  const totales = useMemo(() => {
    const ingresos = ledger.reduce((n, e) => n + (Number(e.precio) || 0), 0);
    const gasto = gastos.reduce((n, e) => n + (Number(e.precio) || 0), 0);
    return { ingresos, gastos: gasto, saldo: ingresos - gasto };
  }, [ledger, gastos]);

  // Evolución mensual: ingresos, gastos y saldo acumulado.
  const porMes = useMemo(() => {
    const map = new Map<string, { ingresos: number; gastos: number }>();
    const acumula = (v: any, importe: number, campo: 'ingresos' | 'gastos') => {
      const ms = toMs(v);
      if (!ms) return;
      const d = new Date(ms);
      const clave = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const acc = map.get(clave) || { ingresos: 0, gastos: 0 };
      acc[campo] += importe;
      map.set(clave, acc);
    };
    ledger.forEach((e) => acumula(e.fechaPago, Number(e.precio) || 0, 'ingresos'));
    gastos.forEach((e) => acumula(e.fecha || e.createdAt, Number(e.precio) || 0, 'gastos'));

    let acumulado = 0;
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-12)
      .map(([clave, v]) => {
        acumulado += v.ingresos - v.gastos;
        const [a, m] = clave.split('-');
        return {
          mes: new Date(+a, +m - 1, 1).toLocaleDateString('es-ES', { month: 'short', year: '2-digit' }),
          Ingresos: Math.round(v.ingresos * 100) / 100,
          Gastos: Math.round(v.gastos * 100) / 100,
          Saldo: Math.round(acumulado * 100) / 100,
        };
      });
  }, [ledger, gastos]);

  const porMetodo = useMemo(() => {
    const map = new Map<string, number>();
    ledger.forEach((e) => map.set(e.metodo || 'otro', (map.get(e.metodo || 'otro') || 0) + (Number(e.precio) || 0)));
    return Array.from(map.entries()).map(([k, v]) => ({ name: metodoLabel(k), value: Math.round(v * 100) / 100, color: metodoColor(k) }));
  }, [ledger]);

  const porProducto = useMemo(() => {
    const map = new Map<string, { total: number; n: number }>();
    ledger.forEach((e) => {
      const k = (e.producto || 'Sin producto').trim();
      const acc = map.get(k) || { total: 0, n: 0 };
      acc.total += Number(e.precio) || 0;
      acc.n += 1;
      map.set(k, acc);
    });
    return Array.from(map.entries())
      .sort((a, b) => b[1].total - a[1].total)
      .slice(0, 8)
      .map(([name, v]) => ({ name, Ingresos: Math.round(v.total * 100) / 100, uds: v.n }));
  }, [ledger]);

  const porCofradia = useMemo(() => {
    const map = new Map<string, number>();
    ledger.forEach((e) => {
      const k = (e.cofradia || 'Sin cofradía').trim();
      map.set(k, (map.get(k) || 0) + (Number(e.precio) || 0));
    });
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [ledger]);

  // Pedidos cobrados que, por lo que sea, no tienen apunte (no debería pasar,
  // pero si la CF falló al escribir la fila conviene verlo).
  const sinApuntar = useMemo(() => {
    const ids = new Set(ledger.map((e) => e.orderId).filter(Boolean));
    return orders.filter((o) =>
      ['en_proceso', 'terminado', 'en_mod', 'entregado'].includes(o.status) && !ids.has(o.id));
  }, [ledger, orders]);

  const cargando = cargandoL || cargandoG;

  return (
    <div className="space-y-5">
      {/* ── Saldo total ── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl border p-6 md:p-7"
        style={{
          borderColor: hexToRgba(totales.saldo >= 0 ? '#34d399' : '#f87171', 0.3),
          background: `linear-gradient(135deg, ${hexToRgba(totales.saldo >= 0 ? '#34d399' : '#f87171', 0.14)}, rgba(255,255,255,0.02))`,
        }}
      >
        <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full blur-3xl"
          style={{ background: hexToRgba(totales.saldo >= 0 ? '#34d399' : '#f87171', 0.2) }} />

        <div className="relative flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="flex items-center gap-2">
              <Coins className="h-4 w-4" style={{ color: totales.saldo >= 0 ? '#34d399' : '#f87171' }} />
              <span className="text-[11px] font-bold uppercase tracking-widest"
                style={{ color: totales.saldo >= 0 ? '#34d399' : '#f87171' }}>
                Saldo total actual
              </span>
            </div>
            <p className="mt-1 font-headline text-4xl font-black md:text-5xl">{eur(totales.saldo)}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {eur(totales.ingresos)} en ingresos − {eur(totales.gastos)} en gastos
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Mini icon={TrendingUp}   label="Ingresos" value={eur(totales.ingresos)} color="#34d399" sub={`${ledger.length} apuntes`} />
            <Mini icon={TrendingDown} label="Gastos"   value={eur(totales.gastos)}   color="#f87171" sub={`${gastos.length} apuntes`} />
            <Mini
              icon={Receipt}
              label="Ticket medio"
              value={eur(ledger.length ? totales.ingresos / ledger.length : 0)}
              color="#38bdf8"
              sub="por venta"
            />
          </div>
        </div>
      </motion.div>

      {sinApuntar.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm">
          <span className="font-bold text-amber-300">
            {sinApuntar.length} pedido(s) cobrado(s) sin apunte en la contabilidad:
          </span>
          <span className="flex flex-wrap gap-1.5">
            {sinApuntar.slice(0, 10).map((o) => (
              <span key={o.id} className="rounded-md bg-amber-500/15 px-1.5 py-0.5 font-mono text-xs font-bold text-amber-200">
                {o.code}
              </span>
            ))}
          </span>
          <span className="text-xs text-muted-foreground">Añádelos a mano con "Nuevo apunte".</span>
        </div>
      )}

      {cargando ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : (
        <>
          {/* ── Gráficas ── */}
          {(porMes.length > 0 || porProducto.length > 0) && (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              {porMes.length > 0 && (
                <Panel titulo="Evolución mensual" icon={BarChart3}>
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={porMes}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                        <XAxis dataKey="mes" tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 11 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 11 }} axisLine={false} tickLine={false} width={52} />
                        <Tooltip
                          cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                          contentStyle={{ background: '#16151a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, fontSize: 12 }}
                          formatter={(v: any) => eur(Number(v))}
                        />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="Ingresos" fill="#34d399" radius={[6, 6, 0, 0]} />
                        <Bar dataKey="Gastos" fill="#f87171" radius={[6, 6, 0, 0]} />
                        <Line type="monotone" dataKey="Saldo" stroke="#facc15" strokeWidth={2.5} dot={{ r: 3 }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </Panel>
              )}

              {porProducto.length > 0 && (
                <Panel titulo="Ingresos por producto" icon={Package}>
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={porProducto} layout="vertical" margin={{ left: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" horizontal={false} />
                        <XAxis type="number" tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 11 }} axisLine={false} tickLine={false} />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={120}
                          tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip
                          cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                          contentStyle={{ background: '#16151a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, fontSize: 12 }}
                          formatter={(v: any, _n: any, p: any) => [`${eur(Number(v))} · ${p.payload.uds} ud.`, 'Ingresos']}
                        />
                        <Bar dataKey="Ingresos" fill="#facc15" radius={[0, 6, 6, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Panel>
              )}

              {porMetodo.length > 0 && (
                <Panel titulo="Cómo pagan" icon={PieIcon}>
                  <div className="h-[240px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={porMetodo} dataKey="value" nameKey="name" innerRadius={52} outerRadius={90} paddingAngle={3}>
                          {porMetodo.map((m, i) => <Cell key={i} fill={m.color} stroke="transparent" />)}
                        </Pie>
                        <Tooltip
                          contentStyle={{ background: '#16151a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, fontSize: 12 }}
                          formatter={(v: any) => eur(Number(v))}
                        />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </Panel>
              )}

              {porCofradia.length > 0 && (
                <Panel titulo="Quién compra" icon={Building2}>
                  <ul className="space-y-2.5">
                    {porCofradia.map(([nombre, total], i) => {
                      const max = porCofradia[0][1] || 1;
                      return (
                        <li key={nombre} className="space-y-1">
                          <div className="flex items-baseline justify-between gap-3 text-sm">
                            <span className="truncate">{nombre}</span>
                            <span className="shrink-0 font-bold text-primary">{eur(total)}</span>
                          </div>
                          <div className="h-1.5 overflow-hidden rounded-full bg-white/8">
                            <div
                              className="h-full rounded-full transition-[width] duration-500"
                              style={{ width: `${(total / max) * 100}%`, background: PALETA[i % PALETA.length] }}
                            />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </Panel>
              )}
            </div>
          )}

          {/* ── Tablas ── */}
          <div className="flex flex-wrap gap-1.5">
            {([
              ['ingresos', `Contabilidad (${ledger.length})`, TrendingUp],
              ['gastos', `Gastos (${gastos.length})`, TrendingDown],
            ] as const).map(([k, label, Icon]) => (
              <button
                key={k}
                onClick={() => setVista(k)}
                className={`flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition-all ${
                  vista === k
                    ? 'border-primary/40 bg-primary/15 text-primary'
                    : 'border-white/8 bg-card/40 text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon className="h-4 w-4" /> {label}
              </button>
            ))}
          </div>

          {vista === 'ingresos' ? <TablaIngresos ledger={ledger} /> : <TablaGastos gastos={gastos} />}
        </>
      )}
    </div>
  );
}

function Mini({ icon: Icon, label, value, color, sub }: {
  icon: React.ElementType; label: string; value: string; color: string; sub?: string;
}) {
  return (
    <div className="rounded-2xl border p-3" style={{ borderColor: hexToRgba(color, 0.22), background: hexToRgba(color, 0.07) }}>
      <div className="flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5" style={{ color }} />
        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color }}>{label}</span>
      </div>
      <p className="mt-1 whitespace-nowrap font-headline text-lg font-black">{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
    </div>
  );
}

function Panel({ titulo, icon: Icon, children }: {
  titulo: string; icon: React.ElementType; children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-white/8 bg-card/40 p-5">
      <h3 className="mb-4 flex items-center gap-2 font-headline text-lg font-bold">
        <Icon className="h-5 w-5 text-primary" /> {titulo}
      </h3>
      {children}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TABLA DE INGRESOS
// ═══════════════════════════════════════════════════════════════════════════

type ColIngreso = 'fechaPago' | 'code' | 'cliente' | 'cofradia' | 'metodo' | 'producto' | 'precio' | 'estado';

const COLS: { key: ColIngreso; label: string; alineado?: string }[] = [
  { key: 'fechaPago', label: 'Fecha de pago' },
  { key: 'code', label: 'Nº de pedido' },
  { key: 'cliente', label: 'Cliente' },
  { key: 'cofradia', label: 'Cofradía' },
  { key: 'metodo', label: 'Método' },
  { key: 'producto', label: 'Producto' },
  { key: 'precio', label: 'Precio', alineado: 'text-right' },
  { key: 'estado', label: 'Estado' },
];

function TablaIngresos({ ledger }: { ledger: PackLedgerEntry[] }) {
  const { toast } = useToast();
  const [busca, setBusca] = useState('');
  const [orden, setOrden] = useState<{ col: ColIngreso; asc: boolean }>({ col: 'fechaPago', asc: false });
  const [editando, setEditando] = useState<PackLedgerEntry | 'new' | null>(null);

  const filas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    const out = q
      ? ledger.filter((e) => [e.code, e.cliente, e.cofradia, e.producto, e.estado]
          .some((v) => String(v || '').toLowerCase().includes(q)))
      : [...ledger];

    const { col, asc } = orden;
    return out.sort((a, b) => {
      const s = asc ? 1 : -1;
      if (col === 'precio') return ((Number(a.precio) || 0) - (Number(b.precio) || 0)) * s;
      if (col === 'fechaPago') return (toMs(a.fechaPago) - toMs(b.fechaPago)) * s;
      return String(a[col] || '').localeCompare(String(b[col] || ''), 'es') * s;
    });
  }, [ledger, busca, orden]);

  const totalVisible = useMemo(() => filas.reduce((n, e) => n + (Number(e.precio) || 0), 0), [filas]);

  const borrar = async (id: string) => {
    const r = await deleteLedgerEntry(id);
    toast({ title: r.success ? 'Eliminado' : 'Error', description: r.message, variant: r.success ? undefined : 'destructive' });
  };

  /** Descarga la tabla tal y como se ve, para llevarla a Excel si hace falta. */
  const exportar = () => {
    const cabeceras = COLS.map((c) => c.label).join(';');
    const cuerpo = filas.map((e) => [
      fechaCorta(e.fechaPago), e.code || '', e.cliente || '', e.cofradia || '',
      metodoLabel(e.metodo), e.producto || '',
      String(Number(e.precio) || 0).replace('.', ','),
      (ESTADOS as any)[e.estado]?.label || e.estado || '',
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(';')).join('\n');

    // BOM para que Excel abra los acentos bien.
    const blob = new Blob([`﻿${cabeceras}\n${cuerpo}`], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `contabilidad-personalizables-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-white/8 bg-card/40">
      <div className="flex flex-wrap items-center gap-3 border-b border-white/8 p-4">
        <h3 className="flex items-center gap-2 font-headline text-lg font-bold">
          <Receipt className="h-5 w-5 text-primary" /> Contabilidad
        </h3>
        <div className="relative min-w-[180px] flex-1 sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar…"
            className="h-9 w-full rounded-xl border border-white/8 bg-black/25 pl-9 pr-3 text-sm outline-none placeholder:text-muted-foreground/60 focus:border-primary/40"
          />
        </div>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={exportar} disabled={filas.length === 0}>
          <Download className="h-3.5 w-3.5" /> CSV
        </Button>
        <Button size="sm" className="gap-1.5" onClick={() => setEditando('new')}>
          <Plus className="h-3.5 w-3.5" /> Nuevo apunte
        </Button>
      </div>

      {/* La tabla scrollea dentro de su caja: la página no se desborda. */}
      <div className="max-h-[600px] overflow-auto">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-[#16151a]">
            <tr>
              {COLS.map((c) => (
                <th
                  key={c.key}
                  onClick={() => setOrden((o) => ({ col: c.key, asc: o.col === c.key ? !o.asc : true }))}
                  className={`cursor-pointer whitespace-nowrap border-b border-white/10 px-3 py-2.5 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground transition-colors hover:text-primary ${c.alineado || ''}`}
                >
                  <span className="inline-flex items-center gap-1">
                    {c.label}
                    <ArrowUpDown className={`h-3 w-3 ${orden.col === c.key ? 'text-primary' : 'opacity-25'}`} />
                  </span>
                </th>
              ))}
              <th className="border-b border-white/10 px-3 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {filas.map((e) => {
              const est = (ESTADOS as any)[e.estado];
              return (
                <tr key={e.id} className="group border-b border-white/5 transition-colors hover:bg-white/[0.03]">
                  <td className="whitespace-nowrap px-3 py-2">{fechaCorta(e.fechaPago)}</td>
                  <td className="whitespace-nowrap px-3 py-2 font-mono text-xs font-bold text-primary">
                    {e.code || <span className="text-muted-foreground/40">manual</span>}
                  </td>
                  <td className="max-w-[160px] truncate px-3 py-2">{e.cliente || '—'}</td>
                  <td className="max-w-[200px] truncate px-3 py-2 text-muted-foreground">{e.cofradia || '—'}</td>
                  <td className="whitespace-nowrap px-3 py-2">
                    <span
                      className="rounded-md px-1.5 py-0.5 text-[11px] font-bold"
                      style={{ color: metodoColor(e.metodo), background: hexToRgba(metodoColor(e.metodo), 0.14) }}
                    >
                      {metodoLabel(e.metodo)}
                    </span>
                  </td>
                  <td className="max-w-[200px] truncate px-3 py-2">{e.producto || '—'}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-mono font-bold text-emerald-400">
                    {eur(e.precio)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    <span
                      className="rounded-md px-1.5 py-0.5 text-[11px] font-semibold"
                      style={est
                        ? { color: est.color, background: hexToRgba(est.color, 0.13) }
                        : { color: 'rgba(255,255,255,0.5)' }}
                    >
                      {est?.short || e.estado || '—'}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-2 py-2 text-right">
                    <div className="flex justify-end gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditando(e)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>¿Eliminar este apunte?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Se borra de la contabilidad y deja de contar en el saldo. El pedido no se toca.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => borrar(e.id)} className="bg-destructive hover:bg-destructive/80">
                              Eliminar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filas.length === 0 && (
              <tr>
                <td colSpan={COLS.length + 1} className="px-3 py-14 text-center text-sm text-muted-foreground">
                  {busca
                    ? 'Ningún apunte coincide con la búsqueda.'
                    : 'Todavía no hay ingresos. Se apuntan solos al confirmar el pago de un pedido.'}
                </td>
              </tr>
            )}
          </tbody>
          {filas.length > 0 && (
            <tfoot className="sticky bottom-0 bg-[#16151a]">
              <tr>
                <td colSpan={6} className="border-t border-white/10 px-3 py-2.5 text-right text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Total {busca ? 'filtrado' : ''}
                </td>
                <td className="border-t border-white/10 px-3 py-2.5 text-right font-mono font-black text-emerald-400">
                  {eur(totalVisible)}
                </td>
                <td colSpan={2} className="border-t border-white/10" />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {editando && (
        <EditorIngreso entry={editando === 'new' ? null : editando} onClose={() => setEditando(null)} />
      )}
    </div>
  );
}

function EditorIngreso({ entry, onClose }: { entry: PackLedgerEntry | null; onClose: () => void }) {
  const { toast } = useToast();
  const [form, setForm] = useState({
    fechaPago: aInputDate(entry?.fechaPago),
    code: entry?.code || '',
    cliente: entry?.cliente || '',
    cofradia: entry?.cofradia || '',
    metodo: entry?.metodo || 'bizum',
    producto: entry?.producto || '',
    precio: entry ? String(entry.precio ?? '') : '',
    estado: entry?.estado || 'en_proceso',
  });
  const [guardando, setGuardando] = useState(false);
  const set = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const guardar = async () => {
    const precio = Number(String(form.precio).replace(',', '.'));
    if (!Number.isFinite(precio) || precio <= 0) {
      toast({ title: 'Precio no válido', description: 'Pon el importe en euros.', variant: 'destructive' });
      return;
    }
    if (!form.producto.trim()) {
      toast({ title: 'Falta el producto', variant: 'destructive' });
      return;
    }
    setGuardando(true);
    const payload = {
      // El `<input type="date">` da texto: se convierte a fecha real, con la
      // hora a mediodía para que el cambio de huso no la mueva de día.
      fechaPago: new Date(`${form.fechaPago}T12:00:00`),
      code: form.code.trim().toUpperCase(),
      cliente: form.cliente.trim(),
      cofradia: form.cofradia.trim(),
      metodo: form.metodo as any,
      producto: form.producto.trim(),
      precio,
      estado: form.estado,
    };
    const r = entry ? await updateLedgerEntry(entry.id, payload) : await createLedgerEntry(payload);
    setGuardando(false);
    toast({ title: r.success ? 'Guardado' : 'Error', description: r.message, variant: r.success ? undefined : 'destructive' });
    if (r.success) onClose();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[90dvh] max-w-lg flex-col gap-0 p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>{entry ? 'Editar apunte' : 'Nuevo apunte'}</DialogTitle>
          <DialogDescription>
            {entry
              ? 'Los apuntes automáticos se crean al confirmar un pago; aquí puedes corregirlos.'
              : 'Añade a mano un ingreso que no venga de un pedido de la tienda.'}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-6 pt-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Fecha de pago</Label>
              <Input type="date" value={form.fechaPago} onChange={(e) => set('fechaPago', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Nº de pedido</Label>
              <Input value={form.code} onChange={(e) => set('code', e.target.value)} placeholder="PZ-XXXXXX (opcional)" className="font-mono" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Cliente</Label>
              <Input value={form.cliente} onChange={(e) => set('cliente', e.target.value)} placeholder="Cuenta de Jartinet" />
            </div>
            <div className="space-y-1.5">
              <Label>Cofradía</Label>
              <Input value={form.cofradia} onChange={(e) => set('cofradia', e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Producto</Label>
            <Input value={form.producto} onChange={(e) => set('producto', e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Método</Label>
              <select
                value={form.metodo}
                onChange={(e) => set('metodo', e.target.value)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus:border-primary/50"
              >
                {METODOS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Precio (€)</Label>
              <Input inputMode="decimal" value={form.precio} onChange={(e) => set('precio', e.target.value)} placeholder="25,00" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Estado</Label>
            <select
              value={form.estado}
              onChange={(e) => set('estado', e.target.value)}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus:border-primary/50"
            >
              {(['en_proceso', 'terminado', 'en_mod', 'entregado', 'cancelado'] as const).map((s) => (
                <option key={s} value={s}>{(ESTADOS as any)[s]?.label || s}</option>
              ))}
            </select>
            {entry && !entry.manual && (
              <p className="text-[11px] text-muted-foreground">
                Este apunte viene de un pedido: su estado se actualiza solo cuando el pedido avanza.
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="border-t border-white/8 px-6 py-4">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={guardar} disabled={guardando}>
            {guardando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TABLA DE GASTOS
// ═══════════════════════════════════════════════════════════════════════════

function TablaGastos({ gastos }: { gastos: PackExpense[] }) {
  const { toast } = useToast();
  const [busca, setBusca] = useState('');
  const [editando, setEditando] = useState<PackExpense | 'new' | null>(null);

  const filas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return q ? gastos.filter((g) => (g.concepto || '').toLowerCase().includes(q)) : gastos;
  }, [gastos, busca]);

  const total = useMemo(() => filas.reduce((n, g) => n + (Number(g.precio) || 0), 0), [filas]);

  const borrar = async (id: string) => {
    const r = await deleteExpense(id);
    toast({ title: r.success ? 'Eliminado' : 'Error', description: r.message, variant: r.success ? undefined : 'destructive' });
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-white/8 bg-card/40">
      <div className="flex flex-wrap items-center gap-3 border-b border-white/8 p-4">
        <h3 className="flex items-center gap-2 font-headline text-lg font-bold">
          <TrendingDown className="h-5 w-5 text-red-400" /> Gastos
        </h3>
        <div className="relative min-w-[180px] flex-1 sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar concepto…"
            className="h-9 w-full rounded-xl border border-white/8 bg-black/25 pl-9 pr-3 text-sm outline-none placeholder:text-muted-foreground/60 focus:border-primary/40"
          />
        </div>
        <Button size="sm" className="gap-1.5" onClick={() => setEditando('new')}>
          <Plus className="h-3.5 w-3.5" /> Nuevo gasto
        </Button>
      </div>

      <div className="max-h-[600px] overflow-auto">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-[#16151a]">
            <tr>
              <th className="border-b border-white/10 px-3 py-2.5 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Fecha</th>
              <th className="border-b border-white/10 px-3 py-2.5 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Concepto</th>
              <th className="border-b border-white/10 px-3 py-2.5 text-right text-xs font-bold uppercase tracking-wider text-muted-foreground">Precio</th>
              <th className="border-b border-white/10 px-3 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {filas.map((g) => (
              <tr key={g.id} className="group border-b border-white/5 transition-colors hover:bg-white/[0.03]">
                <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">{fechaCorta(g.fecha || g.createdAt)}</td>
                <td className="px-3 py-2">{g.concepto || '—'}</td>
                <td className="whitespace-nowrap px-3 py-2 text-right font-mono font-bold text-red-400">−{eur(g.precio)}</td>
                <td className="whitespace-nowrap px-2 py-2 text-right">
                  <div className="flex justify-end gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditando(g)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Eliminar "{g.concepto}"?</AlertDialogTitle>
                          <AlertDialogDescription>Dejará de restar del saldo total.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => borrar(g.id)} className="bg-destructive hover:bg-destructive/80">
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </td>
              </tr>
            ))}
            {filas.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-14 text-center text-sm text-muted-foreground">
                  {busca ? 'Ningún gasto coincide.' : 'Todavía no hay gastos apuntados.'}
                </td>
              </tr>
            )}
          </tbody>
          {filas.length > 0 && (
            <tfoot className="sticky bottom-0 bg-[#16151a]">
              <tr>
                <td colSpan={2} className="border-t border-white/10 px-3 py-2.5 text-right text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Total {busca ? 'filtrado' : ''}
                </td>
                <td className="border-t border-white/10 px-3 py-2.5 text-right font-mono font-black text-red-400">
                  −{eur(total)}
                </td>
                <td className="border-t border-white/10" />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {editando && <EditorGasto expense={editando === 'new' ? null : editando} onClose={() => setEditando(null)} />}
    </div>
  );
}

function EditorGasto({ expense, onClose }: { expense: PackExpense | null; onClose: () => void }) {
  const { toast } = useToast();
  const [concepto, setConcepto] = useState(expense?.concepto || '');
  const [precio, setPrecio] = useState(expense ? String(expense.precio ?? '') : '');
  const [fecha, setFecha] = useState(aInputDate(expense?.fecha || expense?.createdAt));
  const [guardando, setGuardando] = useState(false);

  const guardar = async () => {
    const n = Number(String(precio).replace(',', '.'));
    if (!concepto.trim()) { toast({ title: 'Falta el concepto', variant: 'destructive' }); return; }
    if (!Number.isFinite(n) || n <= 0) {
      toast({ title: 'Precio no válido', description: 'Pon el importe en euros (positivo).', variant: 'destructive' });
      return;
    }
    setGuardando(true);
    const payload = { concepto: concepto.trim(), precio: n, fecha: new Date(`${fecha}T12:00:00`) };
    const r = expense ? await updateExpense(expense.id, payload) : await createExpense(payload);
    setGuardando(false);
    toast({ title: r.success ? 'Guardado' : 'Error', description: r.message, variant: r.success ? undefined : 'destructive' });
    if (r.success) onClose();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{expense ? 'Editar gasto' : 'Nuevo gasto'}</DialogTitle>
          <DialogDescription>Los gastos restan del saldo total.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Concepto</Label>
            <Input value={concepto} onChange={(e) => setConcepto(e.target.value)} placeholder="Ej: Hosting, materiales…" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Precio (€)</Label>
              <Input inputMode="decimal" value={precio} onChange={(e) => setPrecio(e.target.value)} placeholder="12,50" />
            </div>
            <div className="space-y-1.5">
              <Label>Fecha</Label>
              <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={guardar} disabled={guardando}>
            {guardando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
